import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { UserModalService } from 'src/app/services/user-modal/user-modal.service';
import { UserService } from 'src/app/services/user/user.service';
import { Client, User } from 'src/app/shared/models';
import { USER_TYPES } from 'src/app/config/system.config';
import Swal from 'sweetalert2';
import { ClientService } from 'src/app/services/user/client.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { PortService } from 'src/app/services/port/port.service';
import { COUNTRY_LIST } from 'src/app/shared/data/country-list';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
declare var bootstrap: any;

@Component({
  selector: 'app-edit-client-modal',
  templateUrl: './edit-client-modal.component.html',
  styleUrls: ['./edit-client-modal.component.scss']
})
export class EditClientModalComponent extends DestructorComponent implements OnInit, OnDestroy {

  @Output() clientUpdated = new EventEmitter<void>();

  clientForm: FormGroup;
  selectedClient: Client | null = null;
  isLoading: boolean = false;
  imagePreviewUrl: string | null = null;
  selectedImageFile: File | null = null;
  salesPersonList: any[] = [];
  isEditMode: boolean = false;
  primaryImage:string = '';
  imageUploaded = false;
  images: any[] = [];
  selectedFile: File | null = null;
  avatar:string = '';

  // Country and Port related properties
  countryList = COUNTRY_LIST;
  portList: any[] = [];

  constructor(
    private fb: FormBuilder,
    private _userModalService: UserModalService,
    private _userService: UserService,
    private _clientService:ClientService,
    private _globalStateService: GlobalStateService,
    private _generalService:GeneralService,
    private _portService: PortService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.subscribeToUserModalService();
    this.setupModalListener();
    this.setupEditClientListener();
    this.setupAddClientListener();
    this.loadSalesPersonList();
    this.getCountryList();
  }

  getCountryList() {
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countryList = countries || [];
      })
    )
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    // Remove event listeners
    document.removeEventListener('editClient', this.handleEditClient);
    document.removeEventListener('addClient', this.handleAddClient);
  }

  private initializeForm(): void {
    this.clientForm = this.fb.group({
      title: ['Mr.', Validators.required],
      name: ['', Validators.required],
      company: [''],
      country: [''],
      port: [''],
      address: ['',Validators.required],
      state: ['',Validators.required],
      city: [''],
      mobile: ['',Validators.required],
      phone: ['',Validators.required],
      fax: [''],
      skype_id: [''],
      currency_type: ['usd',Validators.required],
      email: ['', [Validators.required, Validators.email]],
      // login_id: ['', Validators.required],
      password: [''],
      status: ['permitted', Validators.required],
      opening_balance: [0],
      opening_balance_type: ['debit'],
      rating: ['gold'],
      sales_person_id: ['',Validators.required],
      // Permissions - using the actual permission structure
      broker: [false],
      auction: [false],
      uss: [false],
      uss_image: [false],
      bids: [false],
      sales_stats: [false],
      show_cif: [false],
      // Custom permissions from form
      show_resale_price: [false],
      bid_permission: ['yes']
    });
  }

  private updatePasswordValidation(): void {
    const passwordControl = this.clientForm.get('password');
    if (passwordControl) {
      if (this.isEditMode) {
        // In edit mode, password is optional
        passwordControl.setValidators([]);
      } else {
        // In add mode, password is required
        passwordControl.setValidators([Validators.required]);
      }
      passwordControl.updateValueAndValidity();
    }
  }

  private setupEditClientListener(): void {
    console.log('Setting up edit client listener');
    // Remove any existing listener first
    document.removeEventListener('editClient', this.handleEditClient);
    // Add the new listener
    document.addEventListener('editClient', this.handleEditClient);
  }

  private setupAddClientListener(): void {
    console.log('Setting up add client listener');
    // Remove any existing listener first
    document.removeEventListener('addClient', this.handleAddClient);
    // Add the new listener
    document.addEventListener('addClient', this.handleAddClient);
  }

  private handleEditClient = (event: any) => {
    console.log('Edit client event received in modal component:', event.detail);
    this.isEditMode = true;
    this.selectedClient = event.detail;
    this.updatePasswordValidation();
    if(this.selectedClient) {
      console.log('Populating form with client:', this.selectedClient);
      this.populateForm(this.selectedClient);
    } else {
      console.warn('No client data received in event');
    }
  }

  private handleAddClient = (_event: any) => {
    console.log('Add client event received in modal component');
    this.isEditMode = false;
    this.selectedClient = null;
    this.updatePasswordValidation();
    this.resetForm();
  }

  private subscribeToUserModalService(): void {
    console.log('Setting up user modal service subscription');
    this.subscriptions.push(
      this._userModalService.selectedUser$.subscribe(user => {
        console.log('User/Client received from service:', user);
        if (user) {
          this.selectedClient = user as Client;
          this.populateForm(this.selectedClient);
        }
      })
    );
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_edit_client');
    console.log('Setting up modal listener, modal element:', modalElement);
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        console.log('Modal opened, checking for client data...');
        // First check service
        const serviceUser = this._userModalService.getSelectedUser();
        if (serviceUser) {
          console.log('Using user/client from service:', serviceUser);
          this.isEditMode = true;
          this.selectedClient = serviceUser as Client;
          this.updatePasswordValidation();
          this.populateForm(this.selectedClient);
        } else if (this.selectedClient) {
          console.log('Using stored selected client:', this.selectedClient);
          this.isEditMode = true;
          this.updatePasswordValidation();
          this.populateForm(this.selectedClient);
        } else {
          console.log('No client data available - add mode');
          this.isEditMode = false;
          this.selectedClient = null;
          this.updatePasswordValidation();
        }
      });
    }
  }

  private populateForm(client: Client): void {
    if (!client) return;
    // Clear any existing image preview first
    this.imagePreviewUrl = client.avatar?client.avatar:null;

    this.clientForm.patchValue({
      title: client.title || 'Mr.',
      name: client.name || '',
      company: client.company || '',
      country: client.country || '',
      port: client.port || '',
      address: client.address || '',
      state: client.state || '',
      city: client.city || '',
      mobile: client.mobile || '',
      phone: client.phone || '',
      fax: client.fax || '',
      skype_id: client.skype_id || '',
      email: client.email || '',
      currency_type: client.currency_type || 'usd',
      login_id: client.login_id || client.email,
      status: client.status || 'permitted',
      opening_balance: client.opening_balance || 0,
      opening_balance_type: client.opening_balance_type || 'debit',
      rating: client.rating || '',
      sales_person_id: client.sales_person_id || '',
      // Map permissions - check for 1 (enabled) or 0 (disabled)
      broker: client.permissions?.broker === 1,
      auction: client.permissions?.auction === 1,
      uss: client.permissions?.uss === 1,
      uss_image: client.permissions?.uss_image === 1,
      bids: client.permissions?.bids === 1,
      sales_stats: client.permissions?.sales_stats === 1,
      show_cif: client.permissions?.show_cif === 1,
      // Custom permissions from form
      salman_dockage: client.salman_dockage || false,
      show_resale_price: client.show_resale_price || false,
      bid_permission: client.bid_permission || 'yes'
    });

    // Load ports if country is selected and preserve the port value
    if (client.country) {
      this.onCountryChange(client.country, true); // Pass true to preserve port value
    }

    // Set image preview if avatar exists
    if (client.avatar) {
      this.imagePreviewUrl = client.avatar;
    }
  }

  loadSalesPersonList(): void {
    this.subscriptions.push(
      this._userService.GetData('1', '', {role_type: USER_TYPES.ADMIN, paginate: 'false'}).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            // Handle non-paginated response structure
            const salesPersons = Array.isArray(response.data) ? response.data : response.data.data || [];
            this.salesPersonList = salesPersons.map((person: User) => ({
              value: person._id || person.doc_id || '',
              label: `${person.title || ''} ${person.name || ''}`.trim()
            }));
          }
        },
        error: (error) => {
          console.error('Error loading sales person list:', error);
          this.salesPersonList = [];
        }
      })
    );
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selectedImageFile = file;
      console.log({ selectedImageFile: this.selectedImageFile,type:file.type });

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreviewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
      this.uploadFile(file);
    }
  }

  removeImage(): void {
    this.selectedImageFile = null;
    this.imagePreviewUrl = null;
    // Reset file input
    const fileInput = document.getElementById('avatar') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onSubmit(): void {
    if (this.clientForm.invalid) {
      // Mark all fields as touched to show validation errors
      console.log("What is this ? ",this.clientForm.errors);
      this.markFormGroupTouched(this.clientForm);
      return;
    }

    if (this.isEditMode && !this.selectedClient) {
      console.error('No client selected for update');
      return;
    }

    this.isLoading = true;
    const formValue = this.clientForm.value;

    // Prepare submit data
    const submitData: any = {
      title: formValue.title,
      name: formValue.name,
      company: formValue.company,
      country: formValue.country,
      port: formValue.port,
      address: formValue.address,
      state: formValue.state,
      city: formValue.city,
      mobile: formValue.mobile,
      phone: formValue.phone,
      currency_type: formValue.currency_type,
      fax: formValue.fax,
      skype_id: formValue.skype_id,
      email: formValue.email,
      login_id: formValue.login_id,
      status: formValue.status,
      opening_balance: formValue.opening_balance,
      opening_balance_type: formValue.opening_balance_type,
      rating: formValue.rating,
      sales_person_id: formValue.sales_person_id,
      avatar: this.avatar,
      // Map permissions to the expected structure
      permissions: {
        broker: formValue.broker ? 1 : 0,
        auction: formValue.auction ? 1 : 0,
        uss: formValue.uss ? 1 : 0,
        uss_image: formValue.uss_image ? 1 : 0,
        bids: formValue.bids ? 1 : 0,
        sales_stats: formValue.sales_stats ? 1 : 0,
        show_cif: formValue.show_cif ? 1 : 0,
        show_resale_price: formValue.show_resale_price,
        bid_permission: formValue.bid_permission
      },
      // Custom fields
    };

    // Only include password if it's provided (for edit mode) or always include for add mode
    if (!this.isEditMode || (formValue.password && formValue.password.trim() !== '')) {
      submitData.password = formValue.password;
    }

    if (this.isEditMode) {
      // Edit mode - update existing client
      const clientId = this.selectedClient!._id || this.selectedClient!.doc_id || "";

      this.subscriptions.push(
        this._clientService.UpdateData(clientId, submitData).subscribe({
          next: (response: any) => {
            this.isLoading = false;

            Swal.fire({
              text: "Client has been successfully updated!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                this.closeModal();
                this.clientUpdated.emit();
                this.resetForm();
              }
            });
          },
          error: (error) => {
            this.isLoading = false;

            Swal.fire({
              text: "Sorry, looks like there are some errors detected, please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      // Add mode - create new client
      this.subscriptions.push(
        this._clientService.AddData(submitData).subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Client created successfully:', response);

            Swal.fire({
              text: "Client has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                this.closeModal();
                this.clientUpdated.emit();
                this.resetForm();
              }
            });
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error creating client:', error);

            Swal.fire({
              text: "Sorry, looks like there are some errors detected, please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    }
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_edit_client');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
    this._userModalService.clearSelectedUser();
  }

  // Country change handler to load ports
  onCountryChange(countryName: string, preservePortValue: boolean = false): void {
    if (countryName) {
      // Find the country code from the country name
      const selectedCountry = this.countryList.find(country => country.name === countryName);
      const countryCode = selectedCountry ? selectedCountry.code : '';

      // Store current port value if we need to preserve it
      const currentPortValue = preservePortValue ? this.clientForm.get('port')?.value : '';

      // Reset the port selection when country changes (unless preserving)
      if (!preservePortValue) {
        this.clientForm.patchValue({ port: '' });
      }
      this.portList = [];

      if (countryCode) {
        // Fetch ports for the selected country
        this.subscriptions.push(
          this._portService.GetData('1', '', { country_code: countryCode, paginate: false }).subscribe({
            next: (response: any) => {
              if (response.status && response.data) {
                // Handle non-paginated response
                this.portList = response.data.map((port: any) => ({
                  value: port._id,
                  label: port.name
                }));

                // If preserving port value, set it after ports are loaded
                if (preservePortValue && currentPortValue) {
                  setTimeout(() => {
                    this.clientForm.patchValue({ port: currentPortValue });
                  }, 100);
                }
              } else {
                this.portList = [];
              }
            },
            error: (error: any) => {
              console.error('Error fetching ports:', error);
              this.portList = [];
            }
          })
        );
      }
    } else {
      // Clear ports if no country is selected
      this.portList = [];
      this.clientForm.patchValue({ port: '' });
    }
  }

  resetForm(): void {
    this.clientForm.reset();
    this.initializeForm();
    this.selectedClient = null;
    this.imagePreviewUrl = null;
    this.selectedImageFile = null;
    this.portList = []; // Reset port list as well
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Public method to open modal in add mode
  openAddModal(): void {
    console.log('Opening modal in add mode');
    this.isEditMode = false;
    this.selectedClient = null;
    this.updatePasswordValidation();
    this.resetForm();

    // Clear any selected user from service
    this._userModalService.clearSelectedUser();

    // Open the modal
    const modalElement = document.getElementById('kt_modal_edit_client');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onFileSelected(event: any) {
    this.selectedFile = event.target.files[0] as File;

    console.log({ selectedFile: this.selectedFile });
    // this.images.pop()

    // this.uploadFile(this.selectedFile);
  }

  uploadFile(file: any) {

    this.imageUploaded = true;
    this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe(res => {

      console.log('res-->', res)
      const imageUrl = res.data.url;
      let formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);


      this._generalService.sendFileToS3(res.data.result.url, formData).subscribe(res => {

        this.imageUploaded = false;

        this.primaryImage =`url('${imageUrl}')`

        this.avatar = imageUrl;
      })

    })
  }
}
